import { createVuetify } from 'vuetify';
import { md3 } from 'vuetify/blueprints';
import { createVueI18nAdapter } from 'vuetify/locale/adapters/vue-i18n';
import i18n from '@/plugins/vueI18n';
import { useI18n } from 'vue-i18n';
import { VBtn } from 'vuetify/components';
import { aliases, mdi } from 'vuetify/iconsets/mdi';
import DayJsAdapter from '@date-io/dayjs';
import enLocale from 'dayjs/locale/en-gb';
import etLocale from 'dayjs/locale/et';
import 'material-symbols/outlined.css';
import '@mdi/font/css/materialdesignicons.css'; // Ensure you are using css-loader
import { mso } from '@/components/iconset/mso';
import { enabledThemesNames } from '@/helpers/theme';

export default createVuetify({
  blueprint: md3,
  date: {
    adapter: DayJsAdapter,
    locale: {
      en: enLocale,
      et: etLocale
    }
  },
  locale: {
    adapter: createVueI18nAdapter({ i18n, useI18n })
  },
  theme: {
    defaultTheme: import.meta.env.VITE_DEFAULT_THEME ?? enabledThemesNames[0] ?? 'blue',
    themes: {
      blue: {
        dark: false,
        colors: {
          primary: '#0651DE',
          'on-primary': '#FFFFFF',
          secondary: '#3D5AA9',
          'on-secondary': '#FFFFFF',
          tertiary: '#C00005',
          'on-tertiary': '#FFFFFF',
          error: '#BA1A1A',
          'alert-error': '#FFCDD9',
          'alert-close': '#45464F',
          'error-border': '#EB1132',
          'error-graphic': '#EB1132',
          info: '#EFF0FF',
          'info-border': '#004C92',
          'info-graphic': '#004C92',
          success: '#008535',
          'success-border': '#48B875',
          'success-graphic': '#D7F5E1',
          warning: '#ffb443',
          'warning-border': '#AD7200',
          'warning-graphic': '#AD7200',
          'on-alert': '#000000',
          'primary-container': '#DBE1FF',
          'on-primary-container': '#00164D',
          'secondary-container': '#DBE1FF',
          'on-secondary-container': '#00174A',
          'tertiary-container': '#DBE1FF',
          'on-tertiary-container': '#FFFFFF',
          'error-container': '#FFDAD6',
          'on-error-container': '#410002',
          'primary-fixed': '#DBE1FF',
          'on-primary-fixed': '#00164D',
          'primary-fixed-dim': '#B5C4FF',
          'on-primary-fixed-dim': '#FFFFFF',
          'secondary-fixed': '#DBE1FF',
          'on-secondary-fixed': '#00174A',
          'secondary-fixed-dim': '#B3C5FF',
          'on-secondary-fixed-dim': '#224290',
          'tertiary-fixed': '#DBE1FF',
          'on-tertiary-fixed': '#FFFFFF',
          'tertiary-fixed-dim': '#EB1132',
          'on-tertiary-fixed-dim': '#930003',
          surface: '#FBF8FD',
          'surface-dim': '#DBD9DD',
          'surface-bright': '#FBF8FD',
          'surface-container-lowest': '#FFFFFF',
          'surface-container-low': '#F5F3F7',
          'surface-container': '#EFEDF1',
          'surface-container-high': '#E9E7EC',
          'surface-container-highest': '#E4E2E6',
          'on-surface': '#1B1B1F',
          'surface-variant': '#D6D6D6',
          'on-surface-variant': '#45464F',
          outline: '#767680',
          'outline-variant': '#C6C6D0',
          'inverse-surface': '#303034',
          'inverse-on-surface': '#F2F0F4',
          'inverse-primary': '#B5C4FF',
          scrim: '#000000',
          // Special cases
          navbar: '#0651DE',
          'navbar-mobile': '#FFFFFF',
          logo: '#FFFFFF',
          'initials-btn': '#FFFFFF',
          'initials-btn-text': '#0651DE',
          'initials-btn-hover': '#0651DE',
          'search-bar': '#FFFFFF',
          'search-bar-text': '#45464F',
          'quick-settings': '#FFFFFF',
          unread: '#DBD9DD',
          'unread-text': '#000000',
          'mailbox-bg': '#FFFFFF',
          'mailbox-list-item-bg': '#FFFFFF',
          'mailbox-list-item-selected-bg': '#E9E7EC',
          'dialog-bg': '#FFFFFF',
          'bimi-verified': '#0651DE',
          'circle-active': '#0651DE'
        }
      },
      'blue-dark': {
        dark: true,
        colors: {
          primary: '#B5C4FF',
          'on-primary': '#000000',
          'primary-container': '#003CAC',
          'on-primary-container': '#DBE1FF',
          'primary-fixed': '#DBE1FF',
          'primary-fixed-dim': '#B5C4FF',
          'on-primary-fixed': '#00164D',
          'on-primary-fixed-variant': '#003CAC',
          'inverse-primary': '#0651DE',
          secondary: '#B3C5FF',
          'on-secondary': '#00297B',
          'secondary-container': '#224290',
          'on-secondary-container': '#DBE1FF',
          'secondary-fixed': '#DBE1FF',
          'secondary-fixed-dim': '#B3C5FF',
          'on-secondary-fixed': '#00174A',
          'on-secondary-fixed-variant': '#224290',
          tertiary: '#00297B',
          'on-tertiary': '#224290',
          'tertiary-container': '#224290',
          'on-tertiary-container': '#B3C5FF',
          'tertiary-fixed': '#B3C5FF',
          'tertiary-fixed-dim': '#DBE1FF',
          'on-tertiary-fixed': '#DBE1FF',
          'on-tertiary-fixed-variant': '#930003',
          surface: '#131316',
          'surface-dim': '#131316',
          'surface-bright': '#39393C',
          'surface-container-lowest': '#0D0E11',
          'surface-container-low': '#1B1B1F',
          'surface-container': '#1F1F23',
          'surface-container-high': '#292A2D',
          'surface-container-highest': '#343438',
          'on-surface': '#C7C6CA',
          'surface-variant': '#676767',
          'on-surface-variant': '#C6C6D0',
          'inverse-surface': '#E4E2E6',
          'inverse-on-surface': '#1B1B1F',
          outline: '#8F909A',
          'outline-variant': '#45464F',
          error: '#FFB4AB',
          'on-error': '#690005',
          'error-container': '#93000A',
          'on-error-container': '#FFDAD6',
          'alert-error': '#93000A',
          'error-border': '#EB1132',
          'error-graphic': '#FFCDD9',
          info: '#004C92',
          'info-border': '#5C90CD',
          'info-graphic': '#DBEBFF',
          success: '#008535',
          'success-border': '#48B875',
          'success-graphic': '#D7F5E1',
          warning: '#AD7200',
          'warning-border': '#C3944D',
          'warning-graphic': '#ffb443',
          'on-alert': '#FFFFFF',
          'alert-close': '#FFFFFF',
          scrim: '#000000',
          // Special cases
          navbar: '#003CAC',
          'navbar-mobile': '#1B1B1F',
          logo: '#FFFFFF',
          'initials-btn': '#FFFFFF',
          'initials-btn-text': '#00297B',
          'initials-btn-hover': '#00297B',
          'search-bar': '#292A2D',
          'search-bar-text': '#C6C6D0',
          'quick-settings': '#39393C',
          unread: '#5E5E62',
          'unread-text': '#FFFFFF',
          'mailbox-bg': '#39393C',
          'mailbox-list-item-bg': '#1F1F23',
          'mailbox-list-item-selected-bg': '#303034',
          'dialog-bg': '#39393C',
          'bimi-verified': '#0651DE',
          'circle-active': '#0651DE'
        }
      },
      red: {
        dark: false,
        colors: {
          primary: '#EC0009',
          'on-primary': '#FFFFFF',
          secondary: '#FFB4A9',
          'on-secondary': '#FFFFFF',
          tertiary: '#C00005',
          'on-tertiary': '#FFFFFF',
          error: '#BA1A1A',
          'alert-error': '#FFCDD9',
          'alert-close': '#45464F',
          'error-border': '#BB002D',
          'error-graphic': '#BB002D',
          info: '#EFF0FF',
          'info-border': '#004C92',
          'info-graphic': '#004C92',
          success: '#008535',
          'success-border': '#48B875',
          'success-graphic': '#D7F5E1',
          warning: '#ffb443',
          'warning-border': '#AD7200',
          'warning-graphic': '#AD7200',
          'on-alert': '#000000',
          'primary-container': '#FFB4A9',
          'on-primary-container': '#00164D',
          'secondary-container': '#FFB4A9',
          'on-secondary-container': '#410000',
          'tertiary-container': '#FFDAD5',
          'on-tertiary-container': '#410000',
          'error-container': '#FFDAD6',
          'on-error-container': '#410002',
          'primary-fixed': '#FFB4A9',
          'on-primary-fixed': '#00164D',
          'primary-fixed-dim': '#B5C4FF',
          'on-primary-fixed-dim': '#FFFFFF',
          'secondary-fixed': '#FFB4A9',
          'on-secondary-fixed': '#410000',
          'secondary-fixed-dim': '#B3C5FF',
          'on-secondary-fixed-dim': '#224290',
          'tertiary-fixed': '#FFDAD5',
          'on-tertiary-fixed': '#410000',
          'tertiary-fixed-dim': '#FFB4A9',
          'on-tertiary-fixed-dim': '#930003',
          surface: '#FBF8FD',
          'surface-dim': '#DBD9DD',
          'surface-bright': '#FBF8FD',
          'surface-container-lowest': '#FFFFFF',
          'surface-container-low': '#F5F3F7',
          'surface-container': '#EFEDF1',
          'surface-container-high': '#E9E7EC',
          'surface-container-highest': '#E4E2E6',
          'on-surface': '#1B1B1F',
          'surface-variant': '#D6D6D6',
          'on-surface-variant': '#45464F',
          outline: '#767680',
          'outline-variant': '#C6C6D0',
          'inverse-surface': '#303034',
          'inverse-on-surface': '#F2F0F4',
          'inverse-primary': '#B5C4FF',
          scrim: '#000000',
          // Special cases
          navbar: '#EC0009',
          'navbar-mobile': '#FFFFFF',
          logo: '#FFFFFF',
          'initials-btn': '#FFFFFF',
          'initials-btn-text': '#EC0009',
          'initials-btn-hover': '#EC0009',
          'search-bar': '#FFFFFF',
          'search-bar-text': '#45464F',
          'quick-settings': '#FFFFFF',
          unread: '#DBD9DD',
          'unread-text': '#000000',
          'mailbox-bg': '#FFFFFF',
          'mailbox-list-item-bg': '#FFFFFF',
          'mailbox-list-item-selected-bg': '#E9E7EC',
          'bimi-verified': '#0651DE',
          'circle-active': '#EC0009'
        }
      },
      // 'red-dark': {
      //   dark: true,
      //   colors: {
      //     primary: '#FFB4A9',
      //     'on-primary': '#000000',
      //     'primary-container': '#EC0009',
      //     'on-primary-container': '#FFDAD5',
      //     'primary-fixed': '#FFDAD5',
      //     'primary-fixed-dim': '#FFB4A9',
      //     'on-primary-fixed': '#00164D',
      //     'on-primary-fixed-variant': '#EC0009',
      //     'inverse-primary': '#0651DE',
      //     secondary: '#FFB4A9',
      //     'on-secondary': '#EC0009',
      //     'secondary-container': '#EC0009',
      //     'on-secondary-container': '#FFDAD5',
      //     'secondary-fixed': '#FFDAD5',
      //     'secondary-fixed-dim': '#B3C5FF',
      //     'on-secondary-fixed': '#00174A',
      //     'on-secondary-fixed-variant': '#EC0009',
      //     tertiary: '#FFB4A9',
      //     'on-tertiary': '#690001',
      //     'tertiary-container': '#930003',
      //     'on-tertiary-container': '#FFDAD5',
      //     'tertiary-fixed': '#FFDAD5',
      //     'tertiary-fixed-dim': '#FFB4A9',
      //     'on-tertiary-fixed': '#410000',
      //     'on-tertiary-fixed-variant': '#930003',
      //     surface: '#131316',
      //     'surface-dim': '#131316',
      //     'surface-bright': '#39393C',
      //     'surface-container-lowest': '#0D0E11',
      //     'surface-container-low': '#1B1B1F',
      //     'surface-container': '#1F1F23',
      //     'surface-container-high': '#292A2D',
      //     'surface-container-highest': '#343438',
      //     'on-surface': '#C7C6CA',
      // 'surface-variant': '#676767',
      //     'on-surface-variant': '#C6C6D0',
      //     'inverse-surface': '#E4E2E6',
      //     'inverse-on-surface': '#1B1B1F',
      //     outline: '#8F909A',
      //     'outline-variant': '#45464F',
      //     error: '#FFB4AB',
      //     'on-error': '#690005',
      //     'error-container': '#93000A',
      //     'on-error-container': '#FFDAD6',
      //     'alert-error': '#93000A',
      //     'error-border': '#EB1132',
      //     'error-graphic': '#FFCDD9',
      //     info: '#004C92',
      //     'info-border': '#5C90CD',
      //     'info-graphic': '#DBEBFF',
      // success: '#008535',
      // 'success-border': '#48B875',
      // 'success-graphic': '#D7F5E1',
      //     warning: '#AD7200',
      //     'warning-border': '#C3944D',
      //     'warning-graphic': '#ffb443',
      //     'on-alert': '#FFFFFF',
      //     'alert-close': '#FFFFFF',
      //     scrim: '#000000',
      //     // Special cases
      //     navbar: '#EC0009',
      //     'navbar-mobile': '#1B1B1F',
      //     logo: '#FFFFFF',
      //     'initials-btn': '#FFFFFF',
      //     'initials-btn-text': '#EC0009',
      //     'initials-btn-hover': '#EC0009',
      //     'mailbox-avatar-text': '#EB1132',
      //     'search-bar': '#292A2D',
      //     'search-bar-text': '#C6C6D0',
      //     'quick-settings': '#39393C',
      //     unread: '#5E5E62',
      //     'unread-text': '#FFFFFF',
      //     'mailbox-bg': '#39393C',
      //     'mailbox-list-item-bg': '#1F1F23',
      //     'mailbox-list-item-selected-bg': '#303034'
      //   }
      // },
      // 'wine-red': {
      //   dark: false,
      //   colors: {
      //     primary: '#FF1637',
      //     'on-primary': '#FFFFFF',
      //     secondary: '#EB1132',
      //     'on-secondary': '#FFFFFF',
      //     tertiary: '#EB1132',
      //     'on-tertiary': '#FFFFFF',
      //     error: '#BA1A1A',
      //     'alert-error': '#FFCDD9',
      //     'alert-close': '#45464F',
      //     'error-border': '#EB1132',
      //     'error-graphic': '#EB1132',
      //     info: '#EFF0FF',
      //     'info-border': '#004C92',
      //     'info-graphic': '#004C92',
      //     success: '#008535',
      //     'success-border': '#48B875',
      //     'success-graphic': '#D7F5E1',
      //     warning: '#ffb443',
      //     'warning-border': '#AD7200',
      //     'warning-graphic': '#AD7200',
      //     'on-alert': '#000000',
      //     'primary-container': '#EB1132',
      //     'on-primary-container': '#00164D',
      //     'secondary-container': '#EB1132',
      //     'on-secondary-container': '#FFFFFF',
      //     'tertiary-container': '#FFDAD5',
      //     'on-tertiary-container': '#FFFFFF',
      //     'error-container': '#FFDAD6',
      //     'on-error-container': '#410002',
      //     'primary-fixed': '#EB1132',
      //     'on-primary-fixed': '#00164D',
      //     'primary-fixed-dim': '#B5C4FF',
      //     'on-primary-fixed-dim': '#003CAC',
      //     'secondary-fixed': '#EB1132',
      //     'on-secondary-fixed': '#FFFFFF',
      //     'secondary-fixed-dim': '#B3C5FF',
      //     'on-secondary-fixed-dim': '#224290',
      //     'tertiary-fixed': '#FFDAD5',
      //     'on-tertiary-fixed': '#FFFFFF',
      //     'tertiary-fixed-dim': '#EB1132',
      //     'on-tertiary-fixed-dim': '#930003',
      //     surface: '#FBF8FD',
      //     'surface-dim': '#DBD9DD',
      //     'surface-bright': '#FBF8FD',
      //     'surface-container-lowest': '#FFFFFF',
      //     'surface-container-low': '#F5F3F7',
      //     'surface-container': '#EFEDF1',
      //     'surface-container-high': '#E9E7EC',
      //     'surface-container-highest': '#E4E2E6',
      //     'on-surface': '#1B1B1F',
      // 'surface-variant': '#D6D6D6',
      //     'on-surface-variant': '#45464F',
      //     outline: '#8F909A',
      //     'outline-variant': '#C6C6D0',
      //     'inverse-surface': '#303034',
      //     'inverse-on-surface': '#F2F0F4',
      //     'inverse-primary': '#B5C4FF',
      //     scrim: '#000000',
      //     // Special cases
      //     navbar: '#EB1132',
      //     'navbar-mobile': '#FFFFFF',
      //     logo: '#FFFFFF',
      //     'initials-btn': '#FFFFFF',
      //     'initials-btn-text': '#FF1637',
      //     'initials-btn-hover': '#FF1637',
      //     'search-bar': '#FFFFFF',
      //     'search-bar-text': '#45464F',
      //     'quick-settings': '#FFFFFF',
      //     unread: '#DBD9DD',
      //     'unread-text': '#000000',
      //     'mailbox-bg': '#FFFFFF',
      //     'mailbox-list-item-bg': '#FFFFFF',
      //     'mailbox-list-item-selected-bg': '#E9E7EC',
      //     'bimi-verified': '#0651DE',
      //     'circle-active': '#FF1637'
      //   }
      // },
      // 'wine-red-dark': {
      'red-dark': {
        dark: true,
        colors: {
          primary: '#EB1132',
          'on-primary': '#000000',
          // 'on-primary': '#FFFFFF',
          'primary-container': '#42080E',
          'on-primary-container': '#FFDAD5',
          'primary-fixed': '#FFDAD5',
          'primary-fixed-dim': '#EB1132',
          'on-primary-fixed': '#00164D',
          'on-primary-fixed-variant': '#FF1637',
          'inverse-primary': '#0651DE',
          secondary: '#EB1132',
          'on-secondary': '#FF1637',
          'secondary-container': '#42080E',
          'on-secondary-container': '#FFDAD5',
          'secondary-fixed': '#FFDAD5',
          'secondary-fixed-dim': '#B3C5FF',
          'on-secondary-fixed': '#00174A',
          'on-secondary-fixed-variant': '#FF1637',
          tertiary: '#EB1132',
          'on-tertiary': '#690001',
          'tertiary-container': '#930003',
          'on-tertiary-container': '#FFDAD5',
          'tertiary-fixed': '#FFDAD5',
          'tertiary-fixed-dim': '#EB1132',
          'on-tertiary-fixed': '#FFFFFF',
          'on-tertiary-fixed-variant': '#930003',
          surface: '#131316',
          'surface-dim': '#131316',
          'surface-bright': '#39393C',
          'surface-container-lowest': '#0D0E11',
          'surface-container-low': '#1B1B1F',
          'surface-container': '#1F1F23',
          'surface-container-high': '#292A2D',
          'surface-container-highest': '#343438',
          'on-surface': '#C7C6CA',
          'surface-variant': '#676767',
          'on-surface-variant': '#C6C6D0',
          'inverse-surface': '#E4E2E6',
          'inverse-on-surface': '#1B1B1F',
          outline: '#8F909A',
          'outline-variant': '#45464F',
          error: '#FFB4AB',
          'on-error': '#690005',
          'error-container': '#93000A',
          'on-error-container': '#FFDAD6',
          'alert-error': '#93000A',
          'error-border': '#EB1132',
          'error-graphic': '#FFCDD9',
          info: '#004C92',
          'info-border': '#5C90CD',
          'info-graphic': '#DBEBFF',
          success: '#008535',
          'success-border': '#48B875',
          'success-graphic': '#D7F5E1',
          warning: '#AD7200',
          'warning-border': '#C3944D',
          'warning-graphic': '#ffb443',
          'on-alert': '#FFFFFF',
          'alert-close': '#FFFFFF',
          scrim: '#000000',
          // Special cases
          navbar: '#42080E',
          'navbar-mobile': '#1B1B1F',
          logo: '#FFFFFF',
          'initials-btn': '#FFFFFF',
          'initials-btn-text': '#93000A',
          'initials-btn-hover': '#42080E',
          'mailbox-avatar-text': '#42080E',
          'search-bar': '#292A2D',
          'search-bar-text': '#C6C6D0',
          'quick-settings': '#39393C',
          unread: '#5E5E62',
          'unread-text': '#FFFFFF',
          'mailbox-bg': '#39393C',
          'mailbox-list-item-bg': '#1F1F23',
          'mailbox-list-item-selected-bg': '#303034',
          'bimi-verified': '#0651DE',
          'circle-active': '#FF1637'
        }
      },
      purple: {
        dark: false,
        colors: {
          primary: '#990AE3',
          'on-primary': '#FFFFFF',
          secondary: '#5C524E',
          'on-secondary': '#FFFFFF',
          tertiary: '#00827C',
          'on-tertiary': '#FFFFFF',
          error: '#E4175C',
          'alert-error': '#FFCDD9',
          'alert-close': '#45464F',
          'error-border': '#E4175C',
          'error-graphic': '#E4175C',
          info: '#E6F5F5',
          'info-border': '#00827C',
          'info-graphic': '#00827C',
          success: '#008535',
          'success-border': '#48B875',
          'success-graphic': '#D7F5E1',
          warning: '#ffb443',
          'warning-border': '#AD7200',
          'warning-graphic': '#AD7200',
          'on-alert': '#000000',
          'primary-container': '#F4E0FF',
          'on-primary-container': '#29003E',
          'secondary-container': '#990AE3',
          'on-secondary-container': '#ffffff',
          'tertiary-container': '#E6F5F5',
          'on-tertiary-container': '#00524F',
          'error-container': '#FFDAD6',
          'on-error-container': '#410002',
          'primary-fixed': '#F4E0FF',
          'on-primary-fixed': '#29003E',
          'primary-fixed-dim': '#D9B4FF',
          'on-primary-fixed-dim': '#FFFFFF',
          'secondary-fixed': '#F8EEE8',
          'on-secondary-fixed': '#5C524E',
          'secondary-fixed-dim': '#ECDED4',
          'on-secondary-fixed-dim': '#3E3632',
          'tertiary-fixed': '#E6F5F5',
          'on-tertiary-fixed': '#00524F',
          'tertiary-fixed-dim': '#00827C',
          'on-tertiary-fixed-dim': '#004D49',
          surface: '#FBF8FD',
          'surface-dim': '#DBD9DD',
          'surface-bright': '#FBF8FD',
          'surface-container-lowest': '#FFFFFF',
          'surface-container-low': '#F5F3F7',
          'surface-container': '#F4F4F9',
          'surface-container-high': '#E9E7EC',
          'surface-container-highest': '#E4E2E6',
          'on-surface': '#121214',
          'surface-variant': '#D6D6D6',
          'on-surface-variant': '#45464F',
          outline: '#767680',
          'outline-variant': '#C6C6D0',
          'inverse-surface': '#303034',
          'inverse-on-surface': '#F2F0F4',
          'inverse-primary': '#D9B4FF',
          scrim: '#000000',
          // Special cases
          navbar: '#29003E',
          'navbar-mobile': '#FFFFFF',
          logo: '#FFFFFF',
          'initials-btn': '#FFFFFF',
          'initials-btn-text': '#990AE3',
          'initials-btn-hover': '#990AE3',
          'search-bar': '#FFFFFF',
          'search-bar-text': '#45464F',
          'quick-settings': '#FFFFFF',
          unread: '#DBD9DD',
          'unread-text': '#000000',
          'mailbox-bg': '#FFFFFF',
          'mailbox-list-item-bg': '#FFFFFF',
          'mailbox-list-item-selected-bg': '#E9E7EC',
          'dialog-bg': '#FFFFFF',
          'bimi-verified': '#990AE3',
          'circle-active': '#990AE3'
        }
      },
      'purple-dark': {
        dark: true,
        colors: {
          primary: '#D9B4FF',
          'on-primary': '#000000',
          'primary-container': '#6200A8',
          'on-primary-container': '#F4E0FF',
          'primary-fixed': '#F4E0FF',
          'primary-fixed-dim': '#D9B4FF',
          'on-primary-fixed': '#29003E',
          'on-primary-fixed-variant': '#6200A8',
          'inverse-primary': '#990AE3',
          secondary: '#ECDED4',
          'on-secondary': '#3E3632',
          'secondary-container': '#990AE3',
          'on-secondary-container': '#ffffff',
          'secondary-fixed': '#F8EEE8',
          'secondary-fixed-dim': '#ECDED4',
          'on-secondary-fixed': '#5C524E',
          'on-secondary-fixed-variant': '#3E3632',
          tertiary: '#00827C',
          'on-tertiary': '#FFFFFF',
          'tertiary-container': '#004D49',
          'on-tertiary-container': '#E6F5F5',
          'tertiary-fixed': '#E6F5F5',
          'tertiary-fixed-dim': '#00827C',
          'on-tertiary-fixed': '#00524F',
          'on-tertiary-fixed-variant': '#004D49',
          surface: '#131316',
          'surface-dim': '#131316',
          'surface-bright': '#39393C',
          'surface-container-lowest': '#0D0E11',
          'surface-container-low': '#1B1B1F',
          'surface-container': '#1F1F23',
          'surface-container-high': '#292A2D',
          'surface-container-highest': '#343438',
          'on-surface': '#C7C6CA',
          'surface-variant': '#676767',
          'on-surface-variant': '#C6C6D0',
          'inverse-surface': '#E4E2E6',
          'inverse-on-surface': '#1B1B1F',
          outline: '#8F909A',
          'outline-variant': '#45464F',
          error: '#FFB4AB',
          'on-error': '#690005',
          'error-container': '#93000A',
          'on-error-container': '#FFDAD6',
          'alert-error': '#93000A',
          'error-border': '#E4175C',
          'error-graphic': '#FFCDD9',
          info: '#004D49',
          'info-border': '#00827C',
          'info-graphic': '#E6F5F5',
          success: '#008535',
          'success-border': '#48B875',
          'success-graphic': '#D7F5E1',
          warning: '#AD7200',
          'warning-border': '#C3944D',
          'warning-graphic': '#ffb443',
          'on-alert': '#FFFFFF',
          'alert-close': '#FFFFFF',
          scrim: '#000000',
          // Special cases
          navbar: '#29003E',
          'navbar-mobile': '#1B1B1F',
          logo: '#FFFFFF',
          'initials-btn': '#FFFFFF',
          'initials-btn-text': '#6200A8',
          'initials-btn-hover': '#6200A8',
          'search-bar': '#292A2D',
          'search-bar-text': '#C6C6D0',
          'quick-settings': '#39393C',
          unread: '#5E5E62',
          'unread-text': '#FFFFFF',
          'mailbox-bg': '#39393C',
          'mailbox-list-item-bg': '#1F1F23',
          'mailbox-list-item-selected-bg': '#303034',
          'dialog-bg': '#39393C',
          'bimi-verified': '#990AE3',
          'circle-active': '#990AE3'
        }
      },
      green: {
        dark: false,
        colors: {
          primary: '#006C4F'
        }
      },
      'green-dark': {
        dark: false,
        colors: {
          primary: '#006C4F'
        }
      },
      gray: {
        dark: false,
        colors: {
          primary: '#303133'
        }
      },
      'gray-dark': {
        dark: false,
        colors: {
          primary: '#303133'
        }
      }
    },
    variations: {
      colors: ['primary', 'secondary', 'tertiary', 'error'],
      lighten: 1,
      darken: 2
    }
  },
  aliases: {
    FilledBtn: VBtn,
    OutlinedBtn: VBtn,
    TextBtn: VBtn,
    ElevatedBtn: VBtn,
    TonalBtn: VBtn,
    CloseBtn: VBtn
  },
  defaults: {
    FilledBtn: {
      color: 'primary',
      variant: 'flat',
      class: 'v-btn--filled',
      ripple: true
    },
    OutlinedBtn: {
      variant: 'outlined',
      class: 'v-btn--outlined',
      ripple: true
    },
    TextBtn: {
      variant: 'text',
      color: 'primary',
      class: 'v-btn--text',
      ripple: true
    },
    ElevatedBtn: {
      variant: 'elevated',
      class: 'v-btn--elevated',
      ripple: true
    },
    TonalBtn: {
      variant: 'tonal',
      class: 'v-btn--tonal',
      ripple: true
    },
    CloseBtn: {
      color: 'on-surface',
      class: 'v-btn--close',
      variant: 'text',
      icon: 'mdi-close',
      height: '40px',
      width: '40px',
      rounded: 'lg'
    },
    VAlert: {
      density: 'compact'
    },
    VBadge: {
      color: 'error',
      textColor: 'onError'
    },
    VCheckbox: {
      color: 'primary',
      density: 'compact'
    },
    VCheckboxBtn: {
      rounded: 'sm',
      density: 'compact'
    },
    VTextField: {
      color: 'primary',
      density: 'compact'
    },
    VCombobox: {
      density: 'compact'
    },
    VSelect: {
      density: 'compact'
    },
    VSwitch: {
      density: 'compact'
    },
    VAutocomplete: {
      density: 'compact'
    },
    VRadio: {
      density: 'compact'
    },
    VRadioGroup: {
      color: 'primary',
      density: 'compact'
    },
    VRangeSlider: {
      color: 'primary'
    },
    VFileInput: {
      color: 'primary'
    },
    VDialog: {
      maxWidth: '552',
      elevation: 0
    },
    VDatePicker: {
      firstDayOfWeek: 1,
      elevation: 0
    },
    VTooltip: {
      location: 'bottom',
      openDelay: 500,
      closeDelay: 500,
      disabled: window.innerWidth < 600
    },
    VTab: {
      rounded: 'sm'
    }
  },
  display: {
    mobileBreakpoint: 'sm',
    thresholds: {
      xs: 360,
      sm: 600,
      md: 905,
      lg: 1240,
      xl: 1440
    }
  },
  icons: {
    defaultSet: 'mdi',
    aliases,
    sets: {
      mso,
      mdi
    }
  },
  components: {}
});
